<template>
    <div
        class="aon-textarea"
        :class="{
            disabled: disabled,
            'error-alert': error,
            'allow-resize': allowResize,
            'width-auto': widthAuto,
        }"
    >
        <div class="label-holder">
            <label
                v-if="label"
                class="mb-1"
                :class="labelCssClass"
            >
                {{ label }}
            </label>
            <span
                class="link"
                v-if="informationIcon"
                v-tooltip="{
                    content: informationIcon,
                    delay: { show: 500, hide: 100 },
                }"
            >
                <font-awesome-icon icon="fal fa-circle-info" class="icon grey02--text" />
            </span>
        </div>
        <p v-if="description" class="mb-1">{{ description }}</p>
        <AonAlertBox
            v-if="error"
            class="mb-2"
            :isContextual="true"
            :title="errorOptions.errorMsg"
            type="error"
        />
        <div class="input-wrapper">
            <textarea
                ref="textareaInputElement"
                :tabindex="disabled ? '-1' : '0'"
                v-model="valueFinal"
                :placeholder="placeholder"
                @keyup="input"
            >
            </textarea>
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref, watch, computed } from 'vue';
import AonAlertBox from '../AonAlertBox.vue';

const props = defineProps({
    modelValue: {
        type: String,
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    labelCssClass: {
        type: String,
        default: null,
    },
    widthAuto: {
        type: Boolean,
        default: false,
    },
    description: {
        type: String,
        default: ''
    },
    id: {
        type: String,
        default: ''
    },
    focus: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    allowResize: {
        type: Boolean,
        default: false,
    },
    informationIcon: {
        type: String,
        default: ''
    },
    darkMode: {
        type: Boolean,
        default: false
    },
    // Hoping to have some sort of validator for errors
    error: {
        type: Boolean,
        default: false,
    },
    errorOptions: {
        type: Object,
        default: () => {
            return {
                errorMsg: ''
            }
        },
    },
    placeholder: {
        type: String,
        default: null
    }
})
const emit = defineEmits(['textarea-input', 'update:modelValue'])

const textareaInputElement = ref(null)

const input = () => {
    emit('textarea-input', valueFinal.value);
    emit('update:modelValue', valueFinal.value);
}

const setFocus = () => {
    textareaInputElement.value.focus();
}

const valueFinal = computed({
    get() {
        return props.modelValue
    },
    set(value) {
        emit('update:modelValue', value)
    }
})

watch(() => props.value, (newVal, oldVal) => {
    props.valueFinal = newVal;
})

onMounted(() => {
    if (props.focus) {
        setFocus();
    }
})

</script>

<script>
    export default {
        name: 'AonTextarea'
    }
</script>

<style lang="scss">
.aon-textarea {
    width: 100%;

    &.width-auto {
        width: auto;
    }

    &.allow-resize {
        textarea {
            resize: both;
        }
    }

    .input-wrapper {
        display: flex;
    }

    textarea {
        resize: none;
        font-size: 1em;
    }

    .label-holder {
        display: flex;
        justify-content: space-between;
    }

    &.dark-mode {
        label {
            color: white;
        }
    }
}
</style>
